
import React, { useContext, useState } from 'react';
import axios from 'axios';
import PropTypes from 'prop-types';

import { connect } from 'react-redux';

import { useSegment } from '../segment/SegmentProvider';
import { clearAddresses } from '../address/addressModule';
import { clearLineItems } from '../lineItem/lineItemModule';
import { useSession } from '../session/SessionProvider';
import { useSettings } from '../mall_settings/SettingsProvider';
import { clearShippingGroups } from '../shipping/shippingModule';
import { dispatchCartUpdateWindowEvent } from '../utils/browser';
import { sanitizePaymentData, serializeOrderRequest } from './mapiOrderSerializers';
import { selectLineItems, getGrandTotal, getTotalShipping } from '../lineItem/lineItemSelectors';

import { mapiHeaders } from './utils';
import { useApplyPromos } from "./ApplyPromosProvider";

export const OrderContext = React.createContext();

export const statusInit = 'INIT';
export const statusStart = 'START';
export const statusError = 'ERROR';
export const statusSuccess = 'SUCCESS';
export const statusProcessing = 'PROC';

export const noResponseError = 'NO_RESPONSE';
export const orderFailedError = 'ORDER_FAILED';
export const overDailyLimitError = 'OVER_DAILY_LIMIT';
export const overPromoLimitError = 'PROMO_LIMITED';
export const paymentMethodError = 'PAYMENT_METHOD_FAILED';
export const missingPaymentInfoError = 'PAYMENT_INFO_MISSING';



export const promoInvalidError = 'PROMO_INVALID_ERROR';

const OrderProvider = ({ lineItems, totalCost, totalShipping, cleanupCart, children }) => {
    const { mapiUrl, orderingToken } = useSettings();
    const { checkoutStarted, orderCompleted } = useSegment();
    const { mapiSession, appId, expireSession } = useSession();
    const { promoDiscounts } = useApplyPromos();
    const { totalDiscountRewardAmount = 0 } = promoDiscounts;

    const [orderStatus, setOrderStatus] = useState(statusInit);
    const [numOrderAttempts, setNumOrderAttempts] = useState(0);
    const [orderError, setOrderError] = useState();
    const [sanitizedPayment, setSanitizedPayment] = useState();
    const [orderNumber, setOrderNumber] = useState();
    const [statement, setStatement] = useState();
    const [orderItems, setOrderItems] = useState(lineItems);

    const setErrorForResponse = (response) => {
        if (response.status === 403) {
            setOrderError(response.data.errorCode === 'PROMO_LIMITED' ? overPromoLimitError : overDailyLimitError);
        } else if (response.status === 400) {
            switch (response.data.error_code) {
                case 'PROMO_NOT_FOUND':
                case 'PROMO_INACTIVE':
                case 'PROMO_SOLD_OUT':
                case 'MISMATCH_AUTH_PROMO_AMOUNT':
                    setOrderError(promoInvalidError);
                    break;
                default:
                    setOrderError(orderFailedError);
            }
        } else {
            setOrderError(orderFailedError);
        }
    };

    const postOrder = async (payment) => {
        const totalCostWithDiscounts = totalCost - totalDiscountRewardAmount;
        setOrderStatus(statusProcessing);
        setOrderError(undefined);
        setOrderItems(lineItems);
        setSanitizedPayment(sanitizePaymentData(payment));
        checkoutStarted(totalCostWithDiscounts, totalShipping, payment.currencyCode, lineItems);
        const orderUrl = `${mapiUrl || ''}/capi/mall/order/`;
        const config = {
            headers: mapiHeaders(orderingToken, mapiSession, appId),
        };
        const orderData = serializeOrderRequest({ payment, cards: lineItems, totalCost: totalCostWithDiscounts, promoDiscounts });

        try {
            const response = await axios.post(orderUrl, orderData, config);
            setOrderNumber(response.data.orderNumber);
            setStatement(response.data.statement);
            setOrderStatus(statusSuccess);
            orderCompleted(response.data.orderNumber, lineItems);
            cleanupCart();
            expireSession();
            dispatchCartUpdateWindowEvent(0);
        } catch (error) {
            if (error.response) {
                setErrorForResponse(error.response);
            } else if (error.request) {
                
                setOrderError(noResponseError);
            } else {
                
                setOrderError(error.toString());
            }
            setOrderStatus(statusError);
        }
        setNumOrderAttempts(numOrderAttempts + 1);
    };

    const placeOrder = (payment) => {
        postOrder(payment).then();
    };

    const paymentMethodFailed = () => {
        setOrderError(paymentMethodError);
        setOrderStatus(statusError);
    };

    const missingPaymentInfo = () => {
        setOrderError(missingPaymentInfoError);
        setOrderStatus(statusError);
    };
    return (
        <OrderContext.Provider
            value={{
                placeOrder,
                paymentMethodFailed,
                missingPaymentInfo,
                orderStatus,
                orderError,
                statement,
                orderNumber,
                orderItems,
                sanitizedPayment,
                setOrderStatus,
                setOrderError,
                orderHasStatusError: orderStatus === statusError,
                orderStatusProcessing: orderStatus === statusProcessing,
                orderStatusSuccess: orderStatus === statusSuccess,
                orderOverDailyLimit: orderError === overDailyLimitError,
                orderHasPromoError: orderStatus === promoInvalidError,
                orderOverPromoRedemptionLimit: orderError === overPromoLimitError,
                orderMissingPaymentInfo: orderError === missingPaymentInfoError
            }}
        >
            {children}
        </OrderContext.Provider>
    );
};

OrderProvider.propTypes = {
    lineItems: PropTypes.arrayOf(PropTypes.object).isRequired,
    totalCost: PropTypes.number.isRequired,
    totalShipping: PropTypes.number.isRequired,
    cleanupCart: PropTypes.func.isRequired,
    children: PropTypes.object.isRequired
};

const mapStateToProps = (state) => ({
    lineItems: selectLineItems(state),
    totalCost: getGrandTotal(state),
    totalShipping: getTotalShipping(state),
});

const mapDispatchToProps = (dispatch) => ({
    cleanupCart: () => {
        dispatch(clearLineItems());
        dispatch(clearShippingGroups());
        dispatch(clearAddresses());
    },
});

export default connect(mapStateToProps, mapDispatchToProps)(OrderProvider);

export const useOrder = () => useContext(OrderContext);
